import React, { useState } from 'react';

const SprintReporterFAQ = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const FAQSection = ({ id, title, children }) => (
    <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <button 
        className={`w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors ${openSections[id] ? 'bg-gray-100' : ''}`}
        onClick={() => toggleSection(id)}
        aria-expanded={openSections[id]}
      >
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <span className="text-xl font-bold text-purple-600">
            {openSections[id] ? '−' : '+'}
          </span>
        </div>
      </button>
      {openSections[id] && (
        <div className="px-6 py-4 bg-white">
          {children}
        </div>
      )}
    </div>
  );

  const FAQItem = ({ question, children }) => (
    <div className="mb-6 pb-6 border-b border-gray-100 last:border-b-0 last:mb-0 last:pb-0">
      <h3 className="text-lg font-medium text-gray-900 mb-3">{question}</h3>
      <div className="text-gray-600">
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Oxanium:wght@400;500;600;700;800;900&display=swap');
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');

        * {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }

        .logo-text {
          font-family: 'Oxanium', monospace;
          font-weight: 900;
          letter-spacing: 0.02em;
        }

        .content-text {
          font-family: 'Avenir Light', 'Avenir', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-weight: 300;
        }

        .hero-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        a {
          text-decoration: none;
        }

        .btn-hover:hover {
          transform: translateY(-2px) scale(1.05);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
        }
      `}</style>

      {/* Navigation */}
      <nav className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <a href="/" className="text-2xl logo-text text-gray-800">
                ZYKAIN
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Header Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-b from-gray-50 via-purple-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl hero-title text-gray-900 mb-6">
            Sprint Reporter FAQ
          </h1>
          <p className="text-lg md:text-xl text-gray-600 content-text max-w-3xl mx-auto leading-relaxed">
            Everything you need to know about automated sprint reporting for agile teams
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <FAQSection id="general" title="General Questions">
            <FAQItem question="What is Sprint Reporter?">
              <p className="mb-4">
                Sprint Reporter is an automated reporting solution for agile teams using Jira and Confluence. 
                It automatically generates comprehensive sprint reports in Confluence when sprints close, 
                providing detailed analytics, team insights, and performance metrics without manual effort.
              </p>
            </FAQItem>

            <FAQItem question="How does Sprint Reporter work?">
              <p className="mb-3">When a sprint closes in Jira, Sprint Reporter automatically:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Collects sprint data including completed stories, velocity, and team performance</li>
                <li>Generates a professional report with analytics and insights</li>
                <li>Publishes the report to your designated Confluence space</li>
                <li>Sends optional Slack notifications to keep your team informed</li>
              </ul>
            </FAQItem>

            <FAQItem question="What products does Sprint Reporter support?">
              <p className="mb-3">Sprint Reporter works with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Jira Cloud</strong> - For sprint data collection and configuration</li>
                <li><strong>Confluence Cloud</strong> - For automated report publishing</li>
                <li><strong>Slack</strong> - For optional team notifications (via webhooks)</li>
              </ul>
            </FAQItem>
          </FAQSection>

          <FAQSection id="installation" title="Installation & Setup">
            <FAQItem question="How do I install Sprint Reporter?">
              <p className="mb-3">Due to Atlassian Marketplace requirements for cross-product apps, Sprint Reporter requires two installations:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Install <strong>Sprint Reporter</strong> from the Atlassian Marketplace in your Jira instance</li>
                <li>Install <strong>Sprint Reporter Companion</strong> in your Confluence instance</li>
                <li>Configure the integration through the Jira admin interface</li>
              </ol>
            </FAQItem>

            <FAQItem question="Do I need separate licenses for both apps?">
              <p className="mb-3">No! Sprint Reporter follows a "freemium" model:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Sprint Reporter (Jira)</strong> - Paid app with full functionality</li>
                <li><strong>Sprint Reporter Companion (Confluence)</strong> - Free companion app for report display</li>
              </ul>
              <p className="mt-3">You only pay for the main Jira app.</p>
            </FAQItem>

            <FAQItem question="Can I try Sprint Reporter before purchasing?">
              <p>
                Yes! Sprint Reporter includes a <strong>30-day free trial</strong> with full functionality. 
                No credit card required to start your trial.
              </p>
            </FAQItem>
          </FAQSection>

          <FAQSection id="configuration" title="Configuration & Usage">
            <FAQItem question="Where are reports published in Confluence?">
              <p className="mb-3">Reports are automatically organized in your chosen Confluence space with a hierarchical structure:</p>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mt-3">
                <pre className="text-sm text-gray-700 font-mono leading-relaxed">{`📁 Your Selected Space
├── 📄 2025 Sprint Reports
│   ├── 📄 PROJECT Sprints
│   │   ├── 📄 Sprint 23 Report
│   │   └── 📄 Sprint 24 Report
│   └── 📄 DEMO Sprints
└── 📄 2024 Sprint Reports`}</pre>
              </div>
            </FAQItem>

            <FAQItem question="How do I choose which issues are included in reports?">
              <p className="mb-3">Sprint Reporter provides flexible filtering options:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Issue Types</strong>: Select which issue types to include (Stories, Bugs, Tasks, etc.)</li>
                <li><strong>Done Statuses</strong>: Define which statuses represent completed work</li>
                <li><strong>Story Point Fields</strong>: Choose your story point custom field for velocity calculations</li>
              </ul>
            </FAQItem>

            <FAQItem question="Can I customize the report format?">
              <p className="mb-3">Sprint Reporter generates professional, standardized reports that include:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sprint summary and completion statistics</li>
                <li>Completed stories breakdown</li>
                <li>Velocity and story point analytics</li>
                <li>Team performance insights</li>
                <li>Sprint timeline and key metrics</li>
              </ul>
              <p className="mt-3">Reports follow best practices for stakeholder communication and retrospective discussions.</p>
            </FAQItem>
          </FAQSection>

          <FAQSection id="slack" title="Slack Integration">
            <FAQItem question="How do I set up Slack notifications?">
              <p className="mb-3">Slack integration is optional and requires:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li>Create an Incoming Webhook in your Slack workspace</li>
                <li>Copy the webhook URL</li>
                <li>Paste it into Sprint Reporter's configuration</li>
                <li>Test the connection</li>
                <li>Your team will receive notifications when sprint reports are generated</li>
              </ol>
            </FAQItem>

            <FAQItem question="Can I disable Slack notifications?">
              <p className="mb-3">Yes! Slack integration is completely optional. You can:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Leave the webhook URL empty to disable notifications</li>
                <li>Remove the webhook URL at any time</li>
                <li>Test notifications before enabling them</li>
              </ul>
            </FAQItem>
          </FAQSection>

          <FAQSection id="security" title="Data & Security">
            <FAQItem question="What data does Sprint Reporter access?">
              <p className="mb-3">Sprint Reporter only accesses:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Jira</strong>: Sprint data, board information, issue details, and story points</li>
                <li><strong>Confluence</strong>: Space listings and page creation capabilities</li>
                <li><strong>No personal data</strong>: We don't store user profiles, emails, or personal information</li>
              </ul>
            </FAQItem>

            <FAQItem question="Is my data secure?">
              <p className="mb-3">Absolutely! Sprint Reporter:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>✅ Runs on Atlassian's secure Forge platform</li>
                <li>✅ Uses OAuth authentication (no password storage)</li>
                <li>✅ Follows enterprise security standards</li>
                <li>✅ Is GDPR compliant with no personal data storage</li>
                <li>✅ Only accesses data you explicitly configure</li>
              </ul>
            </FAQItem>

            <FAQItem question="Where is my data stored?">
              <p>
                Sprint Reporter stores minimal configuration data (selected boards, issue types, etc.) 
                securely on Atlassian's infrastructure. We don't store your actual sprint or issue data - 
                reports are generated in real-time when sprints close.
              </p>
            </FAQItem>
          </FAQSection>

          <FAQSection id="pricing" title="Pricing & Licensing">
            <FAQItem question="How much does Sprint Reporter cost?">
              <p>
                Sprint Reporter follows Atlassian's standard pricing tiers based on your Jira user count. 
                Visit the <a href="https://marketplace.atlassian.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">
                  Atlassian Marketplace
                </a> for current pricing information.
              </p>
            </FAQItem>

            <FAQItem question="Is there a free trial?">
              <p>
                Yes! Every Sprint Reporter installation includes a <strong>30-day free trial</strong> with 
                full functionality. No credit card required.
              </p>
            </FAQItem>

            <FAQItem question="What happens if I don't renew my license?">
              <p className="mb-3">If your license expires:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Sprint Reporter will stop generating new reports</li>
                <li>Existing reports in Confluence remain accessible</li>
                <li>Your configuration settings are preserved</li>
                <li>You can reactivate anytime by renewing your license</li>
              </ul>
            </FAQItem>
          </FAQSection>

          <FAQSection id="troubleshooting" title="Troubleshooting">
            <FAQItem question="Sprint Reporter isn't generating reports. What should I check?">
              <p className="mb-3">Common issues and solutions:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Verify configuration</strong>: Ensure boards, issue types, and Confluence space are selected</li>
                <li><strong>Check permissions</strong>: Confirm you have access to configured boards and Confluence space</li>
                <li><strong>Sprint closure</strong>: Reports are only generated when sprints are officially closed in Jira</li>
                <li><strong>License status</strong>: Verify your Sprint Reporter license is active</li>
              </ol>
            </FAQItem>

            <FAQItem question="I'm not receiving Slack notifications. How do I fix this?">
              <p className="mb-3">Check these common issues:</p>
              <ol className="list-decimal pl-6 space-y-2">
                <li><strong>Webhook URL</strong>: Ensure the Slack webhook URL is correct and active</li>
                <li><strong>Test connection</strong>: Use the "Test Slack Webhook" button in configuration</li>
                <li><strong>Slack permissions</strong>: Verify the webhook has permission to post to your channel</li>
                <li><strong>Sprint closure</strong>: Notifications are only sent when reports are successfully generated</li>
              </ol>
            </FAQItem>

            <FAQItem question="Reports are missing some stories. Why?">
              <p className="mb-3">This usually happens when:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Stories have issue types not included in your configuration</li>
                <li>Stories are in statuses not marked as "Done" in your configuration</li>
                <li>Stories were moved to the sprint after it closed</li>
                <li>Stories don't have the required permissions for the reporting user</li>
              </ul>
            </FAQItem>
          </FAQSection>

          <FAQSection id="support" title="Support">
            <FAQItem question="How do I get help with Sprint Reporter?">
              <p className="mb-3">We provide comprehensive support through:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Email Support</strong>: <a href="mailto:hello@zykain.com" className="text-purple-600 hover:text-purple-700 underline">hello@zykain.com</a></li>
                <li><strong>Documentation</strong>: Complete setup and troubleshooting guides</li>
                <li><strong>Response Time</strong>: Typically within 24 hours for support requests</li>
              </ul>
            </FAQItem>

            <FAQItem question="Do you offer implementation assistance?">
              <p className="mb-3">Yes! We can help with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Initial setup and configuration</li>
                <li>Best practices for sprint reporting</li>
                <li>Troubleshooting complex configurations</li>
                <li>Team training and adoption</li>
              </ul>
            </FAQItem>

            <FAQItem question="How do I request new features?">
              <p className="mb-3">We love hearing from our users! Send feature requests to <a href="mailto:hello@zykain.com" className="text-purple-600 hover:text-purple-700 underline">hello@zykain.com</a> with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Description of the desired feature</li>
                <li>Your use case and business need</li>
                <li>How it would improve your team's workflow</li>
              </ul>
            </FAQItem>
          </FAQSection>

          <FAQSection id="about" title="About Zykain">
            <FAQItem question="Who is Zykain?">
              <p>
                Zykain is a software development company focused on creating productivity tools for agile teams. 
                We specialize in Atlassian ecosystem solutions that streamline workflows and improve team efficiency.
              </p>
            </FAQItem>

            <FAQItem question="Do you have other products?">
              <p>
                Sprint Reporter is our flagship product for agile reporting. 
                Visit <a href="https://www.zykain.com" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">zykain.com</a> to 
                learn about our other productivity solutions and services.
              </p>
            </FAQItem>

            <FAQItem question="How can I stay updated on Sprint Reporter news?">
              <ul className="list-disc pl-6 space-y-2">
                <li>Follow our <a href="https://www.zykain.com/blog" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-700 underline">blog</a> for product updates</li>
                <li>Subscribe to our newsletter for feature announcements</li>
                <li>Connect with us on social media for tips and best practices</li>
              </ul>
            </FAQItem>
          </FAQSection>
        </div>
      </section>

      {/* Other Ways to Get Help Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center hero-title">Other Ways to Get Help</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-gray-900 mb-3">Technical Support</h4>
              <p className="text-sm text-gray-600 mb-4 content-text">
                Need help with a specific issue? Submit a detailed support request
              </p>
              <a href="/support" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                Submit Support Request →
              </a>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-gray-900 mb-3">Email Support</h4>
              <p className="text-sm text-gray-600 mb-4 content-text">
                Send us an email for general inquiries and questions
              </p>
              <a href="mailto:hello@zykain.com" className="text-purple-600 hover:text-purple-700 text-sm font-medium">
                hello@zykain.com →
              </a>
            </div>
            
            <div className="bg-gray-50 p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-gray-900 mb-3">Response Time</h4>
              <p className="text-sm text-gray-600 content-text">
                We typically respond within 24 hours during business days. Priority support for critical issues.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-purple-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6 hero-title">
            Still have questions?
          </h3>
          <p className="text-lg text-gray-600 mb-8 content-text">
            Contact us at <a href="mailto:hello@zykain.com" className="text-purple-600 hover:text-purple-700 underline">hello@zykain.com</a> - we're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://marketplace.atlassian.com" 
              className="bg-purple-600 text-white px-8 py-3 rounded-full hover:bg-purple-700 transition-all shadow-lg font-semibold text-lg btn-hover inline-block"
              target="_blank" 
              rel="noopener noreferrer"
            >
              Try Sprint Reporter Free
            </a>
            <a 
              href="/support" 
              className="bg-white text-purple-600 px-8 py-3 rounded-full hover:bg-gray-50 transition-all shadow-lg font-semibold text-lg border border-purple-600 btn-hover inline-block"
            >
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12" style={{ backgroundColor: "#16163F" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4 logo-text">ZYKAIN</h3>
            <p className="text-gray-400 mb-6">Smart Apps, Smarter Solutions</p>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3">
              <a
                href="/privacy-policy"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Privacy Policy
              </a>
              <a 
                href="/terms-of-service" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Terms of Service
              </a>
              <a 
                href="/support" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Support
              </a>
              <a 
                href="/security-policy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Security Policy
              </a>
              <a 
                href="/sla-policy" 
                className="text-gray-400 hover:text-white transition-colors"
              >
                Service Level Policy
              </a>
              <a
                href="/#contact"
                className="text-gray-400 hover:text-white transition-colors"
              >
                Contact
              </a>
            </div>
            <div className="mt-8 pt-8 border-t border-gray-800">
              <p className="text-gray-500 text-sm">
                © 2025 ZYKAIN. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintReporterFAQ;